import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { WorkflowService } from "./service/workflow.service";
import { Task } from "../../pojo/Task";
import { S25Util } from "../../util/s25-util";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { Workflow } from "./service/Workflow";
import WorkflowItem = Workflow.WorkflowItem;
import { DataIngestionModule } from "../data-ingestion/data.ingestion.module";

@TypeManagerDecorator("my-workflow-page")
@Component({
    selector: "my-workflow-page",
    template: `
        <div class="my-workflow-page">
            <div class="page-header">
                <h1>My Workflow Items</h1>
                <button class="aw-button aw-button--outline" (click)="loadAssignedSubmissions()">Refresh</button>
            </div>

            <div class="workflow-content">
                @if (loading()) {
                    <div class="loading">Loading your workflow items...</div>
                } @else if (assignedSubmissions().length === 0) {
                    <div class="no-items">
                        <h3>No workflow items assigned to you</h3>
                        <p>When form submissions require your approval, they will appear here.</p>
                    </div>
                } @else {
                    <div class="submissions-grid">
                        @for (submission of assignedSubmissions(); track submission.itemId) {
                            <div class="submission-card">
                                <div class="card-header">
                                    <div class="submission-info">
                                        <h3>
                                            {{ submission.name || "Form Submission" }}
                                        </h3>
                                        <div class="submission-meta">
                                            <span class="submission-id">ID: {{ submission.itemId }}</span>
                                            <span
                                                class="status-badge"
                                                [class]="'status-' + submission.workflowStatus?.toLowerCase()"
                                            >
                                                {{ submission.workflowStatus }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="card-actions">
                                        <button
                                            class="aw-button aw-button--outline"
                                            (click)="viewSubmissionDetails(submission)"
                                            [class.active]="selectedSubmission()?.itemId === submission.itemId"
                                        >
                                            {{ selectedSubmission()?.itemId === submission.itemId ? "Hide" : "View" }}
                                            Details
                                        </button>
                                    </div>
                                </div>

                                <div class="card-content">
                                    <div class="submission-summary">
                                        <div class="info-row">
                                            <label>Submitted by:</label>
                                            <span>{{ submission.submitterName || "Unknown" }}</span>
                                        </div>
                                        <div class="info-row">
                                            <label>Submitted on:</label>
                                            <span>{{ formatDate(submission.submissionDate) }}</span>
                                        </div>
                                        <div class="info-row">
                                            <label>Current Stage:</label>
                                            <span>{{ submission.currentWorkflowStage?.stageName || "Unknown" }}</span>
                                        </div>
                                        @if (submission.workflowStatus === "REQUESTED_CHANGES") {
                                            <div class="info-row">
                                                <div class="requested-changes-indicator">
                                                    <span class="changes-badge">Changes Requested</span>
                                                    <small>View History for details</small>
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    <!-- Workflow Actions -->
                                    <div class="workflow-actions">
                                        @if (
                                            submission.workflowStatus === "IN_PROGRESS" ||
                                            submission.workflowStatus === "PENDING"
                                        ) {
                                            <button
                                                class="aw-button aw-button--success"
                                                (click)="approveSubmission(submission)"
                                            >
                                                Approve
                                            </button>
                                            <button
                                                class="aw-button aw-button--danger"
                                                (click)="denySubmission(submission)"
                                            >
                                                Deny
                                            </button>
                                            <button
                                                class="aw-button aw-button--warning"
                                                (click)="requestChanges(submission)"
                                            >
                                                Request Changes
                                            </button>
                                        }
                                        <button
                                            class="aw-button aw-button--outline"
                                            (click)="viewWorkflowHistory(submission)"
                                        >
                                            View History
                                        </button>
                                    </div>
                                </div>

                                <!-- Expanded Details -->
                                @if (selectedSubmission()?.itemId === submission.itemId) {
                                    <div class="expanded-details">
                                        <div class="details-tabs">
                                            <button
                                                class="tab-button"
                                                [class.active]="activeTab() === 'form-data'"
                                                (click)="setActiveTab('form-data')"
                                            >
                                                Form Data
                                            </button>
                                            <button
                                                class="tab-button"
                                                [class.active]="activeTab() === 'history'"
                                                (click)="setActiveTab('history')"
                                            >
                                                History
                                            </button>
                                        </div>

                                        <div class="tab-content">
                                            @if (activeTab() === "form-data") {
                                                <div class="form-data-viewer">
                                                    @if (getSubmissionId(submission)) {
                                                        <s25-ng-ingestion-form
                                                            [submissionId]="getSubmissionId(submission)"
                                                            [readOnly]="true"
                                                            [showCloseButton]="false"
                                                        ></s25-ng-ingestion-form>
                                                    } @else if (submission.workflowSubmissionList?.length) {
                                                        <div class="form-fields">
                                                            @for (
                                                                entry of submission.workflowSubmissionList;
                                                                track entry.itemId
                                                            ) {
                                                                <div class="field-row">
                                                                    <label>{{ entry.itemName || "Field" }}:</label>
                                                                    <div class="field-value">
                                                                        {{ entry.itemValue || "" }}
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    } @else {
                                                        <div class="no-data">No form data available</div>
                                                    }
                                                </div>
                                            }

                                            @if (activeTab() === "history") {
                                                <div class="history-viewer">
                                                    @if (loadingHistory()) {
                                                        <div class="loading">Loading history...</div>
                                                    } @else if (workflowHistory().length === 0) {
                                                        <div class="no-history">No workflow history available</div>
                                                    } @else {
                                                        <div class="history-timeline">
                                                            @for (todo of workflowHistory(); track todo.todoId) {
                                                                <div class="history-item">
                                                                    <div class="history-icon">
                                                                        @if (todo.curTodoState === 2) {
                                                                            <div class="icon approved">✓</div>
                                                                        } @else if (todo.curTodoState === 3) {
                                                                            <div class="icon denied">✗</div>
                                                                        } @else if (todo.curTodoState === 4) {
                                                                            <div class="icon requested-changes">↩</div>
                                                                        } @else if (todo.curTodoState === 99) {
                                                                            <div class="icon cancelled">✗</div>
                                                                        } @else if (todo.curTodoState === 1) {
                                                                            <div class="icon in-progress">⏳</div>
                                                                        }
                                                                    </div>
                                                                    <div class="history-content">
                                                                        <div class="history-title">
                                                                            {{ todo.todoName }}
                                                                        </div>
                                                                        <div class="history-meta">
                                                                            {{
                                                                                formatDate(
                                                                                    todo.compDt || todo.lastModDt
                                                                                )
                                                                            }}
                                                                            by {{ todo.lastModUser }}
                                                                        </div>
                                                                        @if (todo.comment?.commentText) {
                                                                            <div class="history-comment">
                                                                                {{ todo.comment.commentText }}
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            <!-- Request Changes Modal -->
            @if (showRequestChangesModal()) {
                <div class="modal-overlay" (click)="hideRequestChangesModal()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h3>Request Changes</h3>
                            <button class="modal-close" (click)="hideRequestChangesModal()">×</button>
                        </div>
                        <div class="modal-body">
                            <label for="changesText">Please describe the changes needed:</label>
                            <textarea
                                id="changesText"
                                [(ngModel)]="requestedChangesText"
                                class="form-control"
                                rows="4"
                                placeholder="Explain what needs to be changed..."
                            ></textarea>
                        </div>
                        <div class="modal-footer">
                            <button
                                class="aw-button aw-button--primary"
                                (click)="submitRequestChanges()"
                                [disabled]="!requestedChangesText.trim()"
                            >
                                Request Changes
                            </button>
                            <button class="aw-button aw-button--outline" (click)="hideRequestChangesModal()">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .my-workflow-page {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #dee2e6;
        }

        .page-header h1 {
            margin: 0;
            color: #333;
        }

        .loading,
        .no-items {
            text-align: center;
            padding: 60px 20px;
            color: #666;
        }

        .no-items h3 {
            color: #333;
            margin-bottom: 12px;
        }

        .submissions-grid {
            display: grid;
            gap: 20px;
        }

        .submission-card {
            border: 1px solid #dee2e6;
            border-radius: 12px;
            background: white;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: box-shadow 0.2s;
        }

        .submission-card:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .card-header {
            padding: 20px;
            border-bottom: 1px solid #f0f0f0;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        .submission-info h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .submission-meta {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .submission-id {
            color: #666;
            font-size: 14px;
        }

        .status-badge {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
        }

        .status-pending {
            background: #fff3cd;
            color: #856404;
        }

        .status-in_progress {
            background: #d1ecf1;
            color: #0c5460;
        }

        .status-completed {
            background: #d4edda;
            color: #155724;
        }

        .status-denied {
            background: #f8d7da;
            color: #721c24;
        }

        .status-requested_changes {
            background: #ffeaa7;
            color: #856404;
        }

        .card-content {
            padding: 20px;
        }

        .submission-summary {
            margin-bottom: 20px;
        }

        .info-row {
            display: flex;
            margin-bottom: 8px;
            align-items: flex-start;
        }

        .info-row label {
            font-weight: 500;
            color: #555;
            min-width: 120px;
            margin-right: 12px;
        }

        .info-row span {
            color: #333;
        }

        .requested-changes-indicator {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .changes-badge {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 12px;
            padding: 4px 8px;
            color: #856404;
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            align-self: flex-start;
        }

        .requested-changes-indicator small {
            color: #666;
            font-style: italic;
            font-size: 11px;
        }

        .requested-changes {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
            padding: 8px;
            color: #856404;
            font-style: italic;
        }

        .workflow-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
        }

        .expanded-details {
            border-top: 1px solid #f0f0f0;
            background: #f8f9fa;
        }

        .details-tabs {
            display: flex;
            border-bottom: 1px solid #dee2e6;
        }

        .tab-button {
            padding: 12px 20px;
            border: none;
            background: none;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            color: #666;
        }

        .tab-button.active {
            color: #007bff;
            border-bottom-color: #007bff;
        }

        .tab-content {
            padding: 20px;
        }

        .form-fields {
            display: grid;
            gap: 12px;
        }

        .field-row {
            display: grid;
            grid-template-columns: 200px 1fr;
            gap: 12px;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
        }

        .field-row label {
            font-weight: 500;
            color: #555;
        }

        .field-value {
            color: #333;
            word-break: break-word;
        }

        .history-timeline {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .history-item {
            display: flex;
            gap: 12px;
            align-items: flex-start;
        }

        .history-icon {
            flex-shrink: 0;
        }

        .icon {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
        }

        .icon.approved {
            background: #d4edda;
            color: #155724;
        }

        .icon.denied {
            background: #f8d7da;
            color: #721c24;
        }

        .icon.pending {
            background: #d1ecf1;
            color: #0c5460;
        }

        .icon.cancelled {
            background: #fff3cd;
            color: #856404;
        }

        .history-content {
            flex: 1;
        }

        .history-title {
            font-weight: 500;
            color: #333;
            margin-bottom: 4px;
        }

        .history-meta {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
        }

        .history-comment {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 8px;
            color: #333;
            font-style: italic;
        }

        .no-data,
        .no-history {
            text-align: center;
            padding: 40px;
            color: #666;
            font-style: italic;
        }

        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: white;
            border-radius: 8px;
            min-width: 400px;
            max-width: 500px;
            max-height: 80vh;
            overflow-y: auto;
        }

        .modal-header {
            padding: 20px;
            border-bottom: 1px solid #dee2e6;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .modal-header h3 {
            margin: 0;
            color: #333;
        }

        .modal-close {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .modal-body {
            padding: 20px;
        }

        .modal-body label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .modal-footer {
            padding: 20px;
            border-top: 1px solid #dee2e6;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, DataIngestionModule],
})
export class MyWorkflowPageComponent implements OnInit {
    assignedSubmissions = signal<WorkflowItem[]>([]);
    workflowHistory = signal<Task.DBTodo[]>([]);
    availableContacts = signal<any[]>([]);

    loading = signal(false);
    loadingHistory = signal(false);

    selectedSubmission = signal<WorkflowItem | null>(null);
    activeTab = signal<"form-data" | "history">("form-data");

    showRequestChangesModal = signal(false);
    requestedChangesText = "";
    dateFormat: string = "";

    private currentSubmissionForRequestChanges: WorkflowItem | null = null;

    async ngOnInit() {
        this.dateFormat = await UserprefService.getS25DateTimeformat();
        this.loadAssignedSubmissions();
    }

    async loadAssignedSubmissions() {
        this.loading.set(true);
        try {
            const submissions = await WorkflowService.getAssignedSubmissionsWithContactNames();
            this.assignedSubmissions.set(submissions);
        } catch (error) {
            console.error("Error loading assigned submissions:", error);
            S25Util.showError(error);
        } finally {
            this.loading.set(false);
        }
    }

    viewSubmissionDetails(submission: WorkflowItem) {
        const currentSubmission = this.selectedSubmission();
        if (
            currentSubmission &&
            currentSubmission.itemId === submission.itemId &&
            currentSubmission.itemTypeId === submission.itemTypeId
        ) {
            this.selectedSubmission.set(null);
        } else {
            this.selectedSubmission.set(submission);
            this.setActiveTab("form-data");
        }
    }

    setActiveTab(tab: "form-data" | "history") {
        this.activeTab.set(tab);
        if (tab === "history" && this.selectedSubmission()) {
            this.loadWorkflowHistoryForSubmission(this.selectedSubmission()!);
        }
    }

    async viewWorkflowHistory(submission: WorkflowItem) {
        this.selectedSubmission.set(submission);
        this.setActiveTab("history");
    }

    async loadWorkflowHistoryForSubmission(submission: WorkflowItem) {
        this.loadingHistory.set(true);
        try {
            const history = await WorkflowService.getWorkflowHistory(submission.itemTypeId, submission.itemId);
            this.workflowHistory.set(history);
        } catch (error) {
            console.error("Error loading workflow history:", error);
            S25Util.showError(error);
        } finally {
            this.loadingHistory.set(false);
        }
    }

    async approveSubmission(submission: WorkflowItem) {
        if (!confirm("Are you sure you want to approve this submission?")) {
            return;
        }

        try {
            if (!submission.todoId) {
                S25Util.showError("No todo ID found for this submission");
                return;
            }

            const response = await WorkflowService.approveSubmissionFromTodo(submission.todoId);
            await this.loadAssignedSubmissions();
        } catch (error) {
            console.error("Error approving submission:", error);
            S25Util.showError(error);
        }
    }

    async denySubmission(submission: WorkflowItem) {
        if (!confirm("Are you sure you want to deny this submission?")) {
            return;
        }

        try {
            if (!submission.todoId) {
                S25Util.showError("No todo ID found for this submission");
                return;
            }

            const response = await WorkflowService.denySubmissionFromTodo(submission.todoId);
            await this.loadAssignedSubmissions();
            console.log("Submission denied successfully");
        } catch (error) {
            console.error("Error denying submission:", error);
            S25Util.showError(error);
        }
    }

    requestChanges(submission: WorkflowItem) {
        this.currentSubmissionForRequestChanges = submission;
        this.requestedChangesText = "";
        this.showRequestChangesModal.set(true);
    }

    hideRequestChangesModal() {
        this.showRequestChangesModal.set(false);
        this.currentSubmissionForRequestChanges = null;
        this.requestedChangesText = "";
    }

    async submitRequestChanges() {
        if (!this.currentSubmissionForRequestChanges || !this.requestedChangesText.trim()) {
            return;
        }

        try {
            await WorkflowService.requestChangesFromTodo(
                this.currentSubmissionForRequestChanges.todoId,
                this.requestedChangesText.trim(),
            );

            this.hideRequestChangesModal();
            await this.loadAssignedSubmissions();
            console.log("Changes requested successfully");
        } catch (error) {
            console.error("Error requesting changes:", error);
            S25Util.showError(error);
        }
    }

    formatDate(date: string | Date | null): string {
        return S25Datefilter.transform(date, this.dateFormat);
    }

    getSubmissionId(submission: WorkflowItem): number | null {
        // For workflow submissions, the itemId is the submissionId
        if (submission.itemTypeId === Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE) {
            return submission.itemId || null;
        }
        return null;
    }
}
